import { useState } from "react";
import api, { fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Star, Plus, Check, X, LinkSimple, Trash, Image as ImageIcon } from "@phosphor-icons/react";

const empty = { name: "", achievement: "", profile_url: "", photo_file_id: null };

export default function InstitutionHighlights({ inst, isManager, user, onChange }) {
  const notable = inst.notable_alumni || [];
  const approved = notable.filter((n) => n.status !== "suggested");
  const suggested = notable.filter((n) => n.status === "suggested");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  const uploadTo = async (file) => { const fd = new FormData(); fd.append("file", file); const { data } = await api.post("/files/upload", fd); return data.id; };
  const onPhoto = async (e) => { const file = e.target.files?.[0]; if (!file) return; try { const id = await uploadTo(file); setForm((f) => ({ ...f, photo_file_id: id })); } catch { toast.error("Upload failed"); } };
  const onHero = async (e) => { const file = e.target.files?.[0]; if (!file) return; try { const id = await uploadTo(file); await api.put(`/institutions/${inst.id}/hero`, { hero_image_file_id: id }); toast.success("Hero image updated"); onChange && onChange(); } catch { toast.error("Could not set hero image"); } };

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    setBusy(true);
    try {
      await api.post(`/institutions/${inst.id}/notable-alumni`, form);
      toast.success(isManager ? "Notable alumnus added" : "Thanks! An editor will review your suggestion");
      setOpen(false); setForm(empty); onChange && onChange();
    } catch (e) { toast.error("Could not save"); } finally { setBusy(false); }
  };
  const act = async (id, patch) => { try { await api.patch(`/institutions/${inst.id}/notable-alumni/${id}`, patch); onChange && onChange(); } catch { toast.error("Action failed"); } };
  const remove = async (id) => { try { await api.delete(`/institutions/${inst.id}/notable-alumni/${id}`); onChange && onChange(); } catch { toast.error("Could not remove"); } };

  if (!approved.length && !user) return null;

  const PersonCard = ({ n, pending }) => (
    <div className={`flex items-start gap-3 rounded-xl border p-3 ${pending ? "border-amber-200 bg-amber-50/50" : "border-border/60 bg-card"}`} data-testid={`notable-card-${n.id}`}>
      {n.photo_file_id ? <img src={fileUrl(n.photo_file_id)} alt={n.name} className="h-14 w-14 flex-shrink-0 rounded-full object-cover" />
        : <div className="grid h-14 w-14 flex-shrink-0 place-items-center rounded-full bg-muted font-heading text-lg text-muted-foreground">{(n.name || "?")[0]}</div>}
      <div className="min-w-0 flex-1">
        <p className="font-medium leading-tight">{n.name}</p>
        {n.achievement && <p className="text-sm text-muted-foreground">{n.achievement}</p>}
        {n.profile_url && <a href={n.profile_url} target="_blank" rel="noreferrer" className="mt-0.5 inline-flex items-center gap-1 text-xs text-secondary hover:underline"><LinkSimple size={12} /> Profile</a>}
        {pending && n.suggested_by?.name && <p className="mt-1 text-xs text-amber-700">Suggested by {n.suggested_by.name}</p>}
        {pending && isManager && (
          <div className="mt-2 flex gap-2">
            <Button size="sm" className="h-7 rounded-full px-3 text-xs" onClick={() => act(n.id, { status: "approved" })} data-testid={`notable-approve-${n.id}`}><Check size={13} className="mr-1" /> Approve</Button>
            <Button size="sm" variant="outline" className="h-7 rounded-full px-3 text-xs" onClick={() => remove(n.id)} data-testid={`notable-reject-${n.id}`}><X size={13} className="mr-1" /> Reject</Button>
          </div>
        )}
      </div>
      {!pending && isManager && (
        <button onClick={() => remove(n.id)} className="text-muted-foreground hover:text-destructive" title="Remove" data-testid={`notable-remove-${n.id}`}><Trash size={15} /></button>
      )}
    </div>
  );

  return (
    <Card className="mt-6 border-border/60 p-6 shadow-sm" data-testid="inst-highlights">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 font-heading text-xl"><Star size={20} weight="fill" className="text-amber-500" /> Notable alumni</h2>
        <div className="flex flex-wrap gap-2">
          {isManager && (
            <label className="flex cursor-pointer items-center gap-1.5 rounded-full border border-border/60 bg-background px-3 py-1.5 text-sm hover:bg-accent" data-testid="set-hero-label">
              <ImageIcon size={15} /> {inst.hero_image_file_id ? "Change hero" : "Set hero image"}
              <input type="file" accept="image/*" className="hidden" onChange={onHero} data-testid="set-hero-input" />
            </label>
          )}
          {user && (
            <Button size="sm" className="rounded-full gap-1.5" onClick={() => setOpen(true)} data-testid="add-notable-btn">
              <Plus size={15} /> {isManager ? "Add alumnus" : "Suggest an alumnus"}
            </Button>
          )}
        </div>
      </div>

      {approved.length ? (
        <div className="grid gap-3 sm:grid-cols-2">{approved.map((n) => <PersonCard key={n.id} n={n} pending={false} />)}</div>
      ) : (
        <p className="text-sm text-muted-foreground">No notable alumni highlighted yet.{user ? " Be the first to suggest one." : ""}</p>
      )}

      {isManager && suggested.length > 0 && (
        <div className="mt-5" data-testid="notable-suggestions">
          <p className="mb-2 text-sm font-medium text-amber-700">Pending suggestions ({suggested.length})</p>
          <div className="grid gap-3 sm:grid-cols-2">{suggested.map((n) => <PersonCard key={n.id} n={n} pending />)}</div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">{isManager ? "Add a notable alumnus" : "Suggest a notable alumnus"}</DialogTitle>
            <DialogDescription>{isManager ? "This appears immediately on the institution page." : "Editors will review your suggestion before it goes live."}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="notable-name" /></div>
            <div className="space-y-1.5"><Label>Role / achievement</Label><Input value={form.achievement} onChange={(e) => setForm({ ...form, achievement: e.target.value })} placeholder="e.g. Padma Shri awardee · Class of 1998" data-testid="notable-achievement" /></div>
            <div className="space-y-1.5"><Label>Profile link (optional)</Label><Input value={form.profile_url} onChange={(e) => setForm({ ...form, profile_url: e.target.value })} placeholder="https://…" data-testid="notable-profile" /></div>
            <div className="space-y-1.5">
              <Label>Photo (optional)</Label>
              <div className="flex items-center gap-3">
                <label className="flex cursor-pointer items-center gap-1.5 rounded-full border border-border/60 px-3 py-1.5 text-sm hover:bg-accent" data-testid="notable-photo-label"><ImageIcon size={15} /> Upload<input type="file" accept="image/*" className="hidden" onChange={onPhoto} data-testid="notable-photo" /></label>
                {form.photo_file_id && <img src={fileUrl(form.photo_file_id)} alt="" className="h-12 w-12 rounded-full object-cover" />}
              </div>
            </div>
          </div>
          <DialogFooter><Button onClick={submit} disabled={busy} className="rounded-full" data-testid="notable-submit">{busy ? "Saving…" : (isManager ? "Add" : "Send suggestion")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
